import { nextTick } from 'vue';

import { AxiosError } from 'axios';
import { Feature, FeatureCollection, GeoJsonProperties, LineString, Source } from 'geojson';
import { LngLatLike, Map, MapMouseEvent, Popup, PopupOptions } from 'mapbox-gl/dist/mapbox-gl.js';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';

import ApiService from '@/services/ApiService';

import { IProccessingResult } from '../../../shared/types/job';
import enums from '../../../shared/utils/enums';
import confirm from '../helpers/confirm';
import { RootState } from './';

enum PopupType {
  ORDER_SELECTOR,
  ORDER_INFO
}

enum PlanningType {
  ACTUAL,
  PRE_PLAN
}

type State = {
  filterQuery?: Record<string, any>;
  infoPopupAnchor: [number, number] | null; // lngLat of clicked point
  isLoading: boolean;
  loadingText: string | undefined;
  map: Map | null;
  orderInfoPopupFeatures: Feature[];
  orderInfoPopupIndex: number;
  orderSelectorFeatures: Feature[];
  planningError?: string;
  lastPlanningType?: PlanningType;
  mapPlannerJobId?: string;
  planConsideringTimeAndDistance: boolean;
};

const state: State = {
  filterQuery: {},
  infoPopupAnchor: null,
  isLoading: false,
  loadingText: undefined,
  map: null,
  orderInfoPopupFeatures: [],
  orderInfoPopupIndex: 0,
  orderSelectorFeatures: [],
  planningError: undefined,
  lastPlanningType: undefined,
  mapPlannerJobId: undefined,
  planConsideringTimeAndDistance: true
};

const actions: ActionTree<State, RootState> = {
  async setupMap({ commit, dispatch, rootGetters }, map) {
    let depotFeatureCollection = rootGetters['depots/depotsFeatureCollection'];
    if (depotFeatureCollection.features.length === 0) {
      await dispatch('depots/get', null, { root: true });
      depotFeatureCollection = rootGetters['depots/depotsFeatureCollection'];
    }

    map.addSource('depotSource', {
      type: 'geojson',
      data: depotFeatureCollection
    });

    map.addSource('mapPlannerOrdersSource', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] }
    });

    map.addSource('mapPlannerHighlightedOrdersSource', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] }
    });

    map.addSource('mapPlannerFreightsSource', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] }
    });

    const viewConfig = rootGetters['app/viewConfig'];

    map.addLayer({
      id: 'depotLayer',
      source: 'depotSource',
      type: 'circle',
      paint: {
        'circle-color': '#ffffff',
        'circle-stroke-color': `${viewConfig.theme.depot}`,
        'circle-stroke-width': 4,
        'circle-radius': 4
      }
    });

    map.addLayer({
      id: 'selectableOrdersLayer',
      source: 'mapPlannerOrdersSource',
      type: 'circle',
      paint: {
        'circle-color': ['get', 'color'],
        'circle-radius': 8
      },
      filter: ['==', ['get', 'selectable'], true]
    });

    map.addLayer({
      id: 'notSelectableOrdersLayer',
      source: 'mapPlannerOrdersSource',
      type: 'circle',
      paint: {
        'circle-color': ['get', 'color'],
        'circle-radius': 8
      },
      filter: ['==', ['get', 'selectable'], false]
    });

    map.addLayer({
      id: 'highlightedOrdersLayer',
      source: 'mapPlannerHighlightedOrdersSource',
      type: 'circle',
      paint: {
        'circle-color': 'transparent',
        'circle-stroke-color': `${viewConfig.theme.mapSelectionStroke}`,
        'circle-stroke-width': 4
      }
    });

    map.addLayer({
      id: 'freightsLayer',
      source: 'mapPlannerFreightsSource',
      type: 'line',
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 4
      }
    });

    map.on('click', (e) => dispatch('onMapLeftClick', e));
    map.on('contextmenu', (e) => dispatch('onMapRightClick', e));

    map.on('mouseenter', 'selectableOrdersLayer', () => {
      map.getCanvas().style.cursor = 'pointer';
    });

    map.on('mouseleave', 'selectableOrdersLayer', () => {
      map.getCanvas().style.cursor = '';
    });

    const planByDefaultConsideringTimeAndDistance =
      rootGetters['app/mapPlannerSummaryOptions'].planByDefaultConsideringTimeAndDistance;
    await dispatch(
      'setPlanConsideringTimeAndDistance',
      planByDefaultConsideringTimeAndDistance ?? true
    );
    commit('setMap', map);
    await dispatch('getOrderFeatureCollection');
  },

  /**
   * Add a popup to the map
   */
  addPopup(
    { state, dispatch },
    {
      lngLat,
      content,
      options,
      popupType
    }: {
      lngLat: LngLatLike;
      content: HTMLElement;
      options?: Partial<PopupOptions>;
      popupType: PopupType;
    }
  ) {
    if (!state.map) {
      return;
    }

    // remove existing popups
    dispatch('removePopup');

    // clone DOM content
    const clonedContent = content.cloneNode(true) as HTMLElement;
    clonedContent.style.display = 'block';
    clonedContent.id = `${content.id}-cloned`;
    const clonedPopupButtons = Array.from(
      clonedContent.getElementsByClassName('map-planner-popup-toggle-btn')
    ) as HTMLElement[];
    dispatch('addPopupToggleButtonClickListener', { buttons: clonedPopupButtons, popupType });

    if (popupType === PopupType.ORDER_INFO) {
      const clonedPrevButton = clonedContent.getElementsByClassName(
        'map-planner-popup-prev-btn'
      )[0] as HTMLElement;
      const clonedNextButton = clonedContent.getElementsByClassName(
        'map-planner-popup-next-btn'
      )[0] as HTMLElement;

      dispatch('addPopupNavButtonClickListener', {
        prevButton: clonedPrevButton,
        nextButton: clonedNextButton
      });
    }
    new Popup({
      closeButton: false,
      ...(options || {})
    })
      .setLngLat(lngLat)
      .setDOMContent(clonedContent)
      .addTo(state.map);
  },

  addPopupToggleButtonClickListener(
    { state, dispatch },
    { buttons, popupType }: { buttons: HTMLElement[]; popupType: PopupType }
  ) {
    for (const button of buttons) {
      button.addEventListener('click', (event) => {
        const button = event.currentTarget as HTMLElement;
        const orderId = button.dataset.orderId;
        const isSelected = button.classList.contains('map-planner-selected-order');
        if (isSelected) {
          button.classList.remove('map-planner-selected-order');
          const icon = button.getElementsByClassName('mdi-minus')[0];
          if (icon) {
            icon.classList.remove('mdi-minus');
            icon.classList.add('mdi-plus');
          }
          button.title = 'Hozzáadás a kiválasztottakhoz';
        } else {
          button.classList.add('map-planner-selected-order');
          const icon = button.getElementsByClassName('mdi-plus')[0];
          if (icon) {
            icon.classList.remove('mdi-plus');
            icon.classList.add('mdi-minus');
          }
          button.title = 'Eltávolítás a kiválasztottak közül';
        }
        if (orderId) {
          if (popupType === PopupType.ORDER_SELECTOR) {
            dispatch(
              'toggleOrderSelection',
              state.orderSelectorFeatures.find((ftr) => ftr.properties.orderId === orderId)!
            );
          } else if (popupType === PopupType.ORDER_INFO) {
            dispatch(
              'toggleOrderSelection',
              state.orderInfoPopupFeatures.find((ftr) => ftr.properties.orderId === orderId)!
            );
          }
        }
      });
    }
  },

  addPopupNavButtonClickListener(
    { dispatch, getters },
    { prevButton, nextButton }: { prevButton: HTMLElement; nextButton: HTMLElement }
  ) {
    if (getters.showPrevNavButton) {
      prevButton.addEventListener('click', () => {
        dispatch('prevOrderInfoPopupFeature');
      });
    }
    if (getters.showNextNavButton) {
      nextButton.addEventListener('click', () => {
        dispatch('nextOrderInfoPopupFeature');
      });
    }
  },

  /**
   * Remove all popups from the map
   */
  removePopup() {
    document.querySelectorAll('.mapboxgl-popup').forEach((popup) => {
      popup.remove();
    });
  },

  async getOrderFeatureCollection({ commit, state, rootGetters }): Promise<void> {
    commit('setLoading', {
      isLoading: true,
      loadingText: state.mapPlannerJobId ? 'Rendelések betöltése...' : undefined
    });
    try {
      const orderFilter = rootGetters['filter/getFilter']('order') || {};

      // prepare filter for query
      const queryFilter = { ...orderFilter };

      queryFilter.orderId = undefined;
      if (orderFilter.orderId) {
        queryFilter.orderId = orderFilter.orderId.split(',').map((orderId) => orderId.trim());
      }

      queryFilter.orderExternalIds = {};
      if (
        Object.values(orderFilter.orderExternalIds ?? {}).some(
          (value) => value && String(value) && String(value).length > 0
        )
      ) {
        const ordersByExternalIds: Record<string, string[]> = {};
        for (const externalIdName of Object.keys(orderFilter.orderExternalIds)) {
          ordersByExternalIds[externalIdName] = orderFilter.orderExternalIds[externalIdName]
            .split(',')
            .map((externalId: string) => externalId.trim());
        }
        queryFilter.orderExternalIds = ordersByExternalIds;
      }

      const { data: featureCollection } = await ApiService.getOrdersForMapPlanner(queryFilter);
      const source = state.map!.getSource('mapPlannerOrdersSource') as Source;

      source.setData(featureCollection);
      commit('setLoading', { isLoading: false });
    } catch (error) {
      if (error instanceof AxiosError && error.code === 'ERR_CANCELED') {
        return;
      }
      commit('setLoading', { isLoading: false });
      throw error;
    }
  },

  async toggleOrderSelection({ dispatch, state, commit, getters }, feature: Feature) {
    await dispatch('removeFreights');
    commit('setPlanningError', undefined);

    const mapPlannerHighlightedOrdersSource = state.map!.getSource(
      'mapPlannerHighlightedOrdersSource'
    ) as Source;

    const orderId = feature.properties.orderId;

    const isHighlighted = getters.isAlreadySelectedOrder(orderId);

    const updated = isHighlighted
      ? mapPlannerHighlightedOrdersSource._data.features.filter(
          (ftr: Feature) => ftr.properties.orderId !== orderId
        )
      : [...mapPlannerHighlightedOrdersSource._data.features, feature];
    mapPlannerHighlightedOrdersSource.setData({
      type: 'FeatureCollection',
      features: updated
    });
  },

  async removeOrderFromSelection({ dispatch, state, commit }, orderId: string) {
    await dispatch('removeFreights');
    commit('setPlanningError', undefined);

    const mapPlannerHighlightedOrdersSource = state.map!.getSource(
      'mapPlannerHighlightedOrdersSource'
    ) as Source;

    const updated = mapPlannerHighlightedOrdersSource._data.features.filter(
      (ftr: Feature) => ftr.properties.orderId !== orderId
    );
    mapPlannerHighlightedOrdersSource.setData({
      type: 'FeatureCollection',
      features: updated
    });
  },

  async toggleOrderSelectionByFieldAndValue(
    { dispatch },
    { field, value }: { field: string; value: any }
  ) {
    const feature = (state.map!.getSource('mapPlannerOrdersSource') as Source)._data.features.find(
      (ftr: Feature) => ftr.properties[field] === value
    );

    await dispatch('removePopup');
    if (feature) {
      await dispatch('toggleOrderSelection', feature);
    }
    // if no feature found, try to remove from selection by value (as orderId)
    else {
      await dispatch('removeOrderFromSelection', value);
    }
  },

  onMapLeftClick({ state, dispatch }, event: MapMouseEvent) {
    if (!state.map) {
      return;
    }
    const clickedFeatures = state.map.queryRenderedFeatures(event.point, {
      layers: ['highlightedOrdersLayer', 'selectableOrdersLayer']
    });

    /*
    filter clicked features to remove duplicates
    if an orderId appears in both layers, keep only the one from highlightedOrdersLayer
    */
    const features: Feature[] = [];
    const addedOrderIds: Set<string> = new Set();
    for (const layerId of ['highlightedOrdersLayer', 'selectableOrdersLayer']) {
      for (const feature of clickedFeatures) {
        if (feature.layer.id === layerId) {
          const orderId = feature.properties!.orderId;
          if (!addedOrderIds.has(orderId)) {
            features.push(feature);
            addedOrderIds.add(orderId);
          }
        }
      }
    }

    dispatch('removePopup');

    if (features.length === 1) {
      dispatch('toggleOrderSelection', features[0]);
    } else if (features.length > 1) {
      dispatch('setOrderSelectorFeatures', features);

      nextTick(() => {
        dispatch('addPopup', {
          lngLat: event.lngLat,
          content: document.getElementById('orderSelectorPopup') as HTMLElement,
          options: { offset: 10, closeOnClick: false },
          popupType: PopupType.ORDER_SELECTOR
        });
      });
    }
  },

  setOrderSelectorFeatures({ commit }, features: Feature[]) {
    commit('setOrderSelectorFeatures', features);
  },

  async onMapRightClick({ state, dispatch, rootGetters }, event: MapMouseEvent) {
    if (!state.map) {
      return;
    }

    const features = state.map.queryRenderedFeatures(event.point, {
      layers: ['selectableOrdersLayer']
    });

    dispatch('removePopup');

    if (features.length === 0) {
      return;
    }
    dispatch('setOrderInfoPopupFeatures', features);

    state.infoPopupAnchor = event.lngLat as [number, number];
    nextTick(() => {
      dispatch('addPopup', {
        lngLat: state.infoPopupAnchor!,
        content: document.getElementById('orderInfoPopup') as HTMLElement,
        options: {
          maxWidth: rootGetters['app/mapPlannerPopupOptions'].maxWidth,
          offset: 10,
          closeOnClick: false
        },
        popupType: PopupType.ORDER_INFO
      });
    });
  },

  setOrderInfoPopupFeatures({ commit }, features: Feature[]) {
    commit('setOrderInfoPopupFeatures', features);
  },

  nextOrderInfoPopupFeature({ commit, dispatch, rootGetters }) {
    dispatch('removePopup');
    commit('nextOrderInfoPopupFeature');
    nextTick(() => {
      dispatch('addPopup', {
        lngLat: state.infoPopupAnchor!,
        content: document.getElementById('orderInfoPopup') as HTMLElement,
        options: {
          maxWidth: rootGetters['app/mapPlannerPopupOptions'].maxWidth,
          offset: 10,
          closeOnClick: false
        },
        popupType: PopupType.ORDER_INFO
      });
    });
  },

  prevOrderInfoPopupFeature({ commit, dispatch, rootGetters }) {
    dispatch('removePopup');
    commit('prevOrderInfoPopupFeature');
    nextTick(() => {
      dispatch('addPopup', {
        lngLat: state.infoPopupAnchor!,
        content: document.getElementById('orderInfoPopup') as HTMLElement,
        options: {
          maxWidth: rootGetters['app/mapPlannerPopupOptions'].maxWidth,
          offset: 10,
          closeOnClick: false
        },
        popupType: PopupType.ORDER_INFO
      });
    });
  },

  async planFreight({ commit, dispatch, state, rootGetters, rootState }, isPrePlan?: boolean) {
    commit('setLoading', {
      isLoading: true,
      loadingText: `Fuvarok ${isPrePlan ? 'elő' : ''}tervezése...`
    });
    commit('setPlanningError', undefined);

    // if it is an actual planning without pre-planned freights, ask for confirmation
    if (!isPrePlan) {
      const prePlannedFreights = (
        (state.map!.getSource('mapPlannerFreightsSource') as Source)._data as FeatureCollection
      ).features;
      if (prePlannedFreights.length === 0) {
        const answer = await confirm(
          'Előtervezés nélkül indított tényleges tervezést. ' +
            'Ha biztos szeretne lenni, hogy a létrejövő fuvarok megfelelőek-e, ' +
            'indítson előtervezést.<br><br>' +
            '<strong>Biztosan folytatni szeretné a tervezést előtervezés nélkül?</strong>',
          {
            title: 'Előtervezés nélküli tervezés megerősítése',
            width: 700,
            buttonTrueText: 'Igen',
            buttonFalseText: 'Nem',
            color: 'warning'
          }
        );

        if (!answer) {
          commit('setLoading', { isLoading: false });
          return;
        }
      }
    }

    await dispatch('removeFreights');
    commit('setLastPlanningType', isPrePlan ? PlanningType.PRE_PLAN : PlanningType.ACTUAL);

    const orderList = (
      state.map!.getSource('mapPlannerHighlightedOrdersSource') as Source
    )._data.features.map((ftr: Feature) => ftr.properties);

    if (orderList.length === 0) {
      commit('setLoading', { isLoading: false });
      return;
    }

    const uniqueFactoryShortNameIds = [
      ...new Set(orderList.map((order) => order.factoryShortName))
    ];

    let factories = rootState['factories'].factories;

    if (!factories || factories.length === 0) {
      // if factories are not loaded yet, load them
      await dispatch('factories/get', null, { root: true });
      factories = rootState['factories'].factories;
    }

    const uniqueDepotShortNameIds = [
      ...new Set(
        factories
          .filter((factory) => uniqueFactoryShortNameIds.includes(factory.shortName))
          .map((factory) => factory.depotShortName)
      )
    ];

    if (uniqueDepotShortNameIds.length > 1) {
      const message =
        `A választott tervezési mód nem használható eltérő ` +
        `indulási telephellyel rendelkező megrendelések esetén!`;
      await dispatch('alert/error', message, { root: true });
      commit('setLoading', { isLoading: false });
      return;
    }

    // note that partial is stronger than partiallyShipped
    const ordersWithFreights = orderList
      .filter((order) => order.status === enums.orderStatus.partial)
      .map((order) => order.orderId);

    if (ordersWithFreights.length) {
      const answer = await confirm(
        'A kijelölt megrendelések között vannak olyanok, ' +
          'amelyekhez már tartoznak fuvarok.<br><br>' +
          'Ha ezeken a fuvarokon van másik megrendelésből származó raklap is, ' +
          'akkor azok is be lesznek küldve ismételt tervezésre!<br><br>' +
          'Érintett megrendelések: ' +
          ordersWithFreights.join(', ') +
          '<br><br><b>Szeretné feldolgozni a kijelölt megrendeléseket?<b>',
        {
          title: 'Tervezés a kijelölt megrendelésekre',
          width: 700,
          buttonTrueText: 'Igen',
          buttonFalseText: 'Nem'
        }
      );

      if (!answer) {
        commit('setLoading', { isLoading: false });
        return;
      }
    }

    const orderIdList = orderList.map((order) => order.orderId);
    const user = rootGetters['account/user'];
    const userId = user.id;
    try {
      const { data } = await ApiService.calculateNewFreights({
        orderIds: orderIdList,
        userId,
        options: {
          manual: true,
          isPrePlan,
          isMapPlannerJob: true,
          forceMerge: !state.planConsideringTimeAndDistance
        }
      });
      await dispatch('app/setJob', { ...data, isMapPlannerJob: true, isPrePlan }, { root: true });
      commit('setProcessedJobId', data.jobId);
      await dispatch('app/fetchOrdersProcessing', null, { root: true });
    } catch (error) {
      commit('setLoading', { isLoading: false });
      throw error;
    }
  },

  async processPlanningResult({ commit, dispatch, rootState }, result: IProccessingResult) {
    if (result.jobId !== state.mapPlannerJobId) {
      return;
    }
    let message: string | undefined = undefined;
    switch (result.status) {
      case rootState.app.enums.ProcessingStatus.DOES_NOT_FIT:
        message = 'A kijelölt törtfuvarok nem férnek fel egy járműre.';
        break;
      case rootState.app.enums.ProcessingStatus.NO_OVERLAPPING_DELIVERY_DATES:
        message = 'A kijelölt fuvarok szállítási intervallumai nem fedik egymást.';
        break;
      case rootState.app.enums.ProcessingStatus.TOO_MANY_WAYPOINTS:
        message =
          'A fuvarokon túl sok címpont található.\n' +
          'Legfeljebb 9 egyedi címpont fűzhető fel egy fuvarra.';
        break;
      case rootState.app.enums.ProcessingStatus.ROUTE_NOT_FOUND:
        message = 'Nem sikerült útvonalat találni a címpontokhoz.';
        break;
      case rootState.app.enums.ProcessingStatus.NO_VEHICLE:
        message = 'A megadott járműve(kke)l nem lehet teljesíteni a rendelést!';
        break;
      case rootState.app.enums.ProcessingStatus.ORDER_COVERAGE_ERROR:
        message = 'A létrejövő fuvarok nem fednék tökéletesen a feldolgozott rendeléseket.';
        break;
      case rootState.app.enums.ProcessingStatus.ERROR:
        message = 'Ismeretlen hiba történt a tervezés során.';
        break;
      case rootState.app.enums.ProcessingStatus.DONE: {
        //load planned freights to map
        const source = state.map!.getSource('mapPlannerFreightsSource') as Source;
        source.setData(result.featureCollection);
      }
    }
    commit(
      'setPlanningError',
      message === undefined ? undefined : `Sikertelen tervezés:\n ${message}`
    );
    commit('setLoading', { isLoading: false });
    // refresh map
    await dispatch('getOrderFeatureCollection');
  },

  async removeFreights({ commit, state, dispatch }) {
    const source = state.map!.getSource('mapPlannerFreightsSource') as Source;
    source.setData({ type: 'FeatureCollection', features: [] });
    if (state.mapPlannerJobId) {
      await dispatch('app/deleteJobById', state.mapPlannerJobId, { root: true });
    }
    commit('setLastPlanningType', undefined);
  },

  unsetMap({ commit }) {
    commit('setMap', null);
  },

  setPlanConsideringTimeAndDistance({ commit }, value: boolean) {
    commit('setPlanConsideringTimeAndDistance', value);
  }
};

const mutations: MutationTree<State> = {
  setMap(state, map: Map): void {
    state.map = map;
  },

  setOrderSelectorFeatures(state, features: Feature[]): void {
    state.orderSelectorFeatures = features;
  },

  setOrderInfoPopupFeatures(state, features: Feature[]): void {
    state.orderInfoPopupFeatures = features;
    state.orderInfoPopupIndex = 0;
  },

  nextOrderInfoPopupFeature(state): void {
    state.orderInfoPopupIndex += 1;
  },

  prevOrderInfoPopupFeature(state): void {
    state.orderInfoPopupIndex -= 1;
  },

  setPlanningError(state, message?: string): void {
    state.planningError = message;
  },

  setLoading(
    state,
    { isLoading, loadingText }: { isLoading: boolean; loadingText?: string }
  ): void {
    state.isLoading = isLoading;
    if (loadingText !== undefined) {
      state.loadingText = loadingText;
    }
  },

  setLastPlanningType(state, lastPlanningType: PlanningType | undefined): void {
    state.lastPlanningType = lastPlanningType;
  },

  setProcessedJobId(state, jobId: string | undefined): void {
    state.mapPlannerJobId = jobId;
  },

  setPlanConsideringTimeAndDistance(state, value: boolean): void {
    state.planConsideringTimeAndDistance = value;
  }
};

const getters: GetterTree<State, RootState> = {
  freights(state): Feature<LineString, GeoJsonProperties>[] | undefined {
    return state.map!.getSource('mapPlannerFreightsSource').getData().features;
  },
  isOrderSelectorVisible(state): boolean | undefined {
    return state.orderSelectorFeatures.length > 0;
  },

  isOrderInfoPopupVisible(state): boolean | undefined {
    return state.orderInfoPopupFeatures.length > 0;
  },

  isAlreadySelectedOrder:
    (state) =>
    (orderId: string): boolean => {
      const mapPlannerHighlightedOrdersSource = state.map!.getSource(
        'mapPlannerHighlightedOrdersSource'
      ) as Source;
      if (!state.map || !mapPlannerHighlightedOrdersSource) {
        return false;
      }

      const data = mapPlannerHighlightedOrdersSource._data as FeatureCollection;
      return data.features.some((ftr: Feature) => ftr.properties?.orderId === orderId);
    },

  isLoading: (state): boolean => {
    return state.isLoading;
  },

  loadingText: (state): string | undefined => {
    return state.loadingText;
  },

  planningError(state): string | undefined {
    return state.planningError;
  },
  mapPlannerJobId(state): string | undefined {
    return state.mapPlannerJobId;
  },

  wasPrePlan(state): boolean {
    return state.lastPlanningType === PlanningType.PRE_PLAN;
  },

  showNextNavButton(state): boolean {
    return state.orderInfoPopupIndex < state.orderInfoPopupFeatures.length - 1;
  },

  showPrevNavButton(state): boolean {
    return state.orderInfoPopupIndex > 0;
  },

  isPlanningConsideringTimeAndDistance(state): boolean {
    return state.planConsideringTimeAndDistance;
  }
};

export const ordersMapPlanner: Module<State, RootState> = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
};
